import {
	ThinkingOrb,
	type OrbSize,
	type OrbState,
} from "thinking-orbs";

type TPaulThinkingIndicatorProps = {
	status?: string | null;
	message: string;
	detail?: string | null;
	variant?: "hero" | "inline" | "icon" | "panel";
	size?: OrbSize;
	className?: string;
};

const DEBUG_STATUSES = new Set([
	"DEBUG_PREPARING",
	"DEBUG_THINKING",
	"DEBUG_EVALUATING",
]);

const STATUS_ORB_STATES: Partial<Record<string, OrbState>> =
	{
		PENDING: "connecting",
		NOT_YET_STARTED: "connecting",
		PREPARING: "shaping",
		THINKING: "solving",
		EVALUATING: "searching",
		RUNNING: "working",
		IN_PROGRESS: "working",
	};

const ORB_SPEEDS: Record<OrbState, number> = {
	working: 1,
	searching: 1.05,
	solving: 0.9,
	listening: 0.9,
	connecting: 0.85,
	weaving: 0.85,
	composing: 0.9,
	breathing: 0.8,
	shaping: 0.85,
};

const includesAny = (
	value: string,
	keywords: Array<string>
): boolean =>
	keywords.some((keyword) => value.includes(keyword));

const normalizeCopy = (value: string): string =>
	value
		.trim()
		.toLowerCase()
		.replace(/[.!?\u2026]+$/u, "")
		.trim();

export const getPaulOrbState = (
	status?: string | null,
	message?: string | null
): OrbState => {
	const normalizedStatus =
		status?.trim().toUpperCase() ?? "";
	if (DEBUG_STATUSES.has(normalizedStatus)) {
		return "weaving";
	}

	const exactState = STATUS_ORB_STATES[normalizedStatus];
	if (exactState) {
		return exactState;
	}

	const activity =
		`${status ?? ""} ${message ?? ""}`.toLowerCase();
	if (
		includesAny(activity, [
			"connect",
			"initial",
			"load",
			"start",
			"sync",
			"await",
		])
	) {
		return "connecting";
	}
	if (
		includesAny(activity, [
			"prepar",
			"setup",
			"setting up",
			"shap",
		])
	) {
		return "shaping";
	}
	if (
		includesAny(activity, [
			"evaluat",
			"review",
			"search",
			"retriev",
			"gather",
			"lookup",
			"validat",
			"check",
		])
	) {
		return "searching";
	}
	if (
		includesAny(activity, [
			"compos",
			"writ",
			"generat",
			"format",
			"finaliz",
		])
	) {
		return "composing";
	}
	if (
		includesAny(activity, [
			"think",
			"analy",
			"reason",
			"solv",
			"process",
		])
	) {
		return "solving";
	}

	return "working";
};

const PaulThinkingIndicator = ({
	status,
	message,
	detail,
	variant = "inline",
	size = variant === "hero" ? 64 : 20,
	className = "",
}: TPaulThinkingIndicatorProps) => {
	const orbState = getPaulOrbState(status, message);
	const visibleDetail =
		detail &&
		normalizeCopy(detail) !== normalizeCopy(message)
			? detail
			: null;
	const classes = [
		"paul-thinking-indicator",
		`paul-thinking-indicator--${variant}`,
		className,
	]
		.filter(Boolean)
		.join(" ");

	return (
		<div
			className={classes}
			role="status"
			aria-live={variant === "icon" ? "off" : "polite"}
			data-paul-state={orbState}
		>
			<div
				className="paul-thinking-indicator__orb"
				aria-hidden="true"
			>
				<ThinkingOrb
					state={orbState}
					size={size}
					theme={variant === "panel" ? "dark" : "light"}
					speed={ORB_SPEEDS[orbState]}
				/>
			</div>
			{variant !== "icon" ? (
				<div className="paul-thinking-indicator__copy">
					<span>{message}</span>
					{visibleDetail ? (
						<small>{visibleDetail}</small>
					) : null}
				</div>
			) : (
				<span className="paul-thinking-indicator__sr-only">
					{message}
				</span>
			)}
		</div>
	);
};

export default PaulThinkingIndicator;
